import { useEffect, useRef, useState } from 'react';
import type { ClipboardEvent, KeyboardEvent, ReactNode } from 'react';
import Sidebar from '../components/layout/Sidebar';
import PageHeader from '../components/PageHeader';
import { supabase } from '../lib/supabase';
import { formatFileSize } from '../components/chat/useChatAttachments';
import { useSearchParams } from 'react-router-dom';
import { Button, Card, Icon, Input, PulseDot, SearchableSelect, SegmentedControl } from '../components/ui';
import { useUser } from '../context/UserContext';
import { enqueueContentJob } from '../lib/contentJobs';
import { AI_JOB_MODELS, aiJobModelLabel } from '../lib/aiJobs';
import AttachmentDropzone from '../components/ai/AttachmentDropzone';
import ModelPicker from '../components/ai/ModelPicker';
import { useAttachmentPicker } from '../components/ai/attachmentPicker';
import PiecesPanel from '../components/content/PiecesPanel';

/**
 * Content — brief intake for agent-generated pieces.
 * Queue for generation creates a fulfillment task and a content-kind AI job.
 */

const DRAFT_KEY = 'cockpit.content.draft';

interface Draft {
  title: string;
  primary: string;
  secondary: string[];
  clientId: string | null;
  repoId: string | null;
  /** Pinned LLM for the queue job; null = agent default. */
  model: string | null;
}

interface ClientOption {
  id: string;
  name: string;
}

interface RepoOption {
  id: string;
  name: string;
  repo_url: string;
}

const emptyDraft = (): Draft => ({
  title: '',
  primary: '',
  secondary: [],
  clientId: null,
  repoId: null,
  model: null,
});

/** Text fields survive a reload; File objects can't be serialised, so attachments don't. */
function readDraft(): Draft {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return emptyDraft();
    const parsed = JSON.parse(raw) as Partial<Draft>;
    return {
      title: typeof parsed.title === 'string' ? parsed.title : '',
      primary: typeof parsed.primary === 'string' ? parsed.primary : '',
      secondary: Array.isArray(parsed.secondary)
        ? parsed.secondary.filter((s): s is string => typeof s === 'string')
        : [],
      clientId: typeof parsed.clientId === 'string' ? parsed.clientId : null,
      // a repo only makes sense with its client attached
      repoId:
        typeof parsed.clientId === 'string' && typeof parsed.repoId === 'string' ? parsed.repoId : null,
      // only a still-allowlisted model survives a reload
      model:
        typeof parsed.model === 'string' && AI_JOB_MODELS.some((m) => m.id === parsed.model)
          ? parsed.model
          : null,
    };
  } catch {
    return emptyDraft();
  }
}

const normalizePhrase = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase();
const splitPhrases = (raw: string) =>
  raw
    .split(/[,;\n]+/)
    .map((s) => s.trim().replace(/\s+/g, ' '))
    .filter(Boolean);

/* ---------- field chrome ---------- */

function Field({
  label,
  aside,
  hint,
  children,
}: {
  label: string;
  aside?: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '7px' }}>
        <span className="t-eyebrow">{label}</span>
        {aside}
      </div>
      {children}
      {hint ? (
        <div style={{ marginTop: '6px', fontSize: 'var(--text-xs)', color: 'var(--text-faint)', lineHeight: 1.5 }}>
          {hint}
        </div>
      ) : null}
    </div>
  );
}

function CountAside({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
      {count}
    </span>
  );
}

/* ---------- secondary phrases: chip field ---------- */

function PhraseChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        height: '24px',
        padding: '0 4px 0 9px',
        maxWidth: '100%',
        borderRadius: 'var(--radius-pill)',
        background: 'var(--surface-active)',
        border: '1px solid var(--border-hairline)',
        color: 'var(--text-body)',
        fontSize: 'var(--text-sm)',
      }}
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      <button
        type="button"
        className="pressable"
        aria-label={`Remove ${label}`}
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '18px',
          height: '18px',
          padding: 0,
          border: 'none',
          borderRadius: 'var(--radius-pill)',
          background: 'transparent',
          color: 'var(--text-faint)',
          cursor: 'pointer',
        }}
      >
        <Icon name="x" size={12} />
      </button>
    </span>
  );
}

interface PhraseFieldProps {
  phrases: string[];
  onChange: (next: string[]) => void;
  /** The primary phrase — never allowed in as a secondary. */
  reserved: string;
  placeholder: string;
  hint: string;
}

function PhraseField({ phrases, onChange, reserved, placeholder, hint }: PhraseFieldProps) {
  const [text, setText] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const commit = (raw: string) => {
    const incoming = splitPhrases(raw);
    setText('');
    if (incoming.length === 0) return;

    const seen = new Set(phrases.map(normalizePhrase));
    const reservedKey = normalizePhrase(reserved);
    const added: string[] = [];
    let skipped: string | null = null;

    for (const phrase of incoming) {
      const key = normalizePhrase(phrase);
      if (reservedKey && key === reservedKey) {
        skipped = 'That’s already the primary phrase';
        continue;
      }
      if (seen.has(key)) {
        skipped = 'Already added';
        continue;
      }
      seen.add(key);
      added.push(phrase);
    }

    if (added.length > 0) onChange([...phrases, ...added]);
    setNotice(skipped);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit(text);
      return;
    }
    if (e.key === 'Backspace' && text === '' && phrases.length > 0) {
      onChange(phrases.slice(0, -1));
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text');
    if (/[,;\n]/.test(pasted)) {
      e.preventDefault();
      commit(`${text}${pasted}`);
    }
  };

  return (
    <div>
      <div
        onClick={() => inputRef.current?.focus()}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '6px',
          minHeight: 'var(--control-md)',
          padding: '5px 8px',
          background: focused ? 'var(--surface-raised)' : 'var(--surface-input)',
          border: `1px solid ${focused ? 'var(--border-focus)' : 'var(--border-hairline)'}`,
          borderRadius: 'var(--radius-sm)',
          cursor: 'text',
          transition: 'border-color var(--dur-base) var(--ease-out), background var(--dur-base) var(--ease-out)',
        }}
      >
        {phrases.map((phrase) => (
          <PhraseChip
            key={phrase}
            label={phrase}
            onRemove={() => onChange(phrases.filter((p) => p !== phrase))}
          />
        ))}
        <input
          ref={inputRef}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            if (notice) setNotice(null);
          }}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            commit(text);
          }}
          placeholder={phrases.length > 0 ? 'Add another…' : placeholder}
          aria-label="Secondary search phrase"
          style={{
            flex: '1 1 160px',
            minWidth: '120px',
            height: '24px',
            padding: '0 4px',
            border: 'none',
            background: 'transparent',
            outline: 'none',
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--text-base)',
            color: 'var(--text-body)',
          }}
        />
      </div>
      <div
        style={{
          marginTop: '6px',
          fontSize: 'var(--text-xs)',
          color: notice ? 'var(--status-warn)' : 'var(--text-faint)',
          lineHeight: 1.5,
          transition: 'color var(--dur-base) var(--ease-out)',
        }}
      >
        {notice ?? hint}
      </div>
    </div>
  );
}

/* ---------- summary: readiness ---------- */

function ReadyRow({
  label,
  value,
  ok,
  optional = false,
}: {
  label: string;
  value: string;
  ok: boolean;
  optional?: boolean;
}) {
  const tone = ok ? 'up' : optional ? 'idle' : 'warn';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '9px 0',
        borderBottom: '1px solid var(--border-hairline)',
      }}
    >
      <PulseDot tone={tone} title={ok ? 'Set' : optional ? 'Optional' : 'Required'} />
      <span style={{ flex: 1, fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>{label}</span>
      <span
        title={value}
        style={{
          maxWidth: '55%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: 'var(--text-xs)',
          color: ok ? 'var(--text-muted)' : 'var(--text-faint)',
        }}
      >
        {value}
      </span>
    </div>
  );
}

/* ---------- the page ---------- */

export default function Content() {
  const { user } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const view: 'brief' | 'pieces' = searchParams.get('tab') === 'pieces' ? 'pieces' : 'brief';
  const setView = (next: string) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'pieces') params.set('tab', 'pieces');
    else params.delete('tab');
    setSearchParams(params, { replace: true });
  };
  const [draft, setDraft] = useState<Draft>(readDraft);
  const { files, error: fileError, add: addFiles, remove: removeFile, clear: clearFiles } = useAttachmentPicker();
  const [confirmClear, setConfirmClear] = useState(false);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [repos, setRepos] = useState<RepoOption[]>([]);
  const [reposLoading, setReposLoading] = useState(false);
  const [queueBusy, setQueueBusy] = useState(false);
  const [queueError, setQueueError] = useState<string | null>(null);
  const [queuedId, setQueuedId] = useState<string | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    } catch {
      // localStorage unavailable — the draft simply won't survive a reload
    }
  }, [draft]);

  useEffect(() => {
    let cancelled = false;
    supabase
      .from('clients')
      .select('id, name')
      .order('name')
      .then(({ data, error }) => {
        if (!cancelled && !error) setClients((data as ClientOption[]) || []);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const clientId = draft.clientId;
  useEffect(() => {
    if (!clientId) {
      setRepos([]);
      return;
    }
    let cancelled = false;
    setReposLoading(true);
    supabase
      .from('client_repos')
      .select('id, name, repo_url')
      .eq('client_id', clientId)
      .order('name')
      .then(({ data, error }) => {
        if (cancelled) return;
        setReposLoading(false);
        const rows = !error && data ? (data as RepoOption[]) : [];
        setRepos(rows);
        // stale draft or deleted repo — drop a selection that no longer exists
        setDraft((d) => (d.repoId && !rows.some((r) => r.id === d.repoId) ? { ...d, repoId: null } : d));
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const patch = (partial: Partial<Draft>) => setDraft((d) => ({ ...d, ...partial }));

  const clearDraft = () => {
    setDraft(emptyDraft());
    clearFiles();
    setConfirmClear(false);
    setQueueError(null);
    setQueuedId(null);
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch {
      // ignore
    }
  };

  const handleQueue = async () => {
    if (queueBusy) return;
    setQueueError(null);
    setQueuedId(null);
    if (!title || !primary) {
      setQueueError('Add a title and a primary search phrase.');
      return;
    }
    if (!selectedClient) {
      setQueueError('Pick a client so the writer can load Voice from the knowledge base.');
      return;
    }
    if (!user?.id) {
      setQueueError('Sign in to queue this brief.');
      return;
    }
    setQueueBusy(true);
    try {
      const job = await enqueueContentJob({
        title,
        primary,
        secondary: draft.secondary,
        clientId: selectedClient.id,
        clientName: selectedClient.name,
        repoId: selectedRepo?.id ?? null,
        repoName: selectedRepo?.name ?? null,
        repoUrl: selectedRepo?.repo_url ?? null,
        files: files.map((f) => f.file),
        createdBy: { id: user.id, name: user.name || user.email },
        model: draft.model,
      });
      setQueuedId(job.id);
      setDraft(emptyDraft());
      clearFiles();
      try {
        localStorage.removeItem(DRAFT_KEY);
      } catch {
        // ignore
      }
    } catch (err) {
      setQueueError(err instanceof Error ? err.message : 'Failed to queue this brief');
    } finally {
      setQueueBusy(false);
    }
  };

  const title = draft.title.trim();
  const primary = draft.primary.trim();
  const ready = title.length > 0 && primary.length > 0;
  const hasAnything =
    draft.title.length > 0 ||
    draft.primary.length > 0 ||
    draft.secondary.length > 0 ||
    files.length > 0 ||
    draft.clientId !== null;
  const selectedClient = clients.find((c) => c.id === draft.clientId) ?? null;
  const selectedRepo = repos.find((r) => r.id === draft.repoId) ?? null;
  const totalBytes = files.reduce((sum, f) => sum + f.file.size, 0);
  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

  return (
    <div className="min-h-screen">
      <Sidebar />

      <main className="px-4 md:px-8 pb-24 md:pb-8 md:ml-[72px]">
        <PageHeader
          title="Content"
          actions={
            <SegmentedControl
              options={[
                { id: 'brief', label: 'New brief' },
                { id: 'pieces', label: 'Pieces' },
              ]}
              value={view}
              onChange={setView}
            />
          }
        />

        {view === 'pieces' ? (
          <div className="ck-stagger">
            <PiecesPanel clients={clients} />
          </div>
        ) : (
        <div
          className="ck-stagger grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] items-start"
          style={{ gap: 'var(--space-5)' }}
        >
          {/* brief */}
          <Card
            padding="var(--space-6)"
            eyebrow="New brief"
            actions={
              hasAnything ? (
                <span
                  className="t-eyebrow"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: 'var(--text-faint)' }}
                >
                  <Icon name="check" size={11} />
                  Draft saved
                </span>
              ) : null
            }
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
              <Field label="Title">
                <input
                  type="text"
                  value={draft.title}
                  onChange={(e) => patch({ title: e.target.value })}
                  placeholder="Working title for the piece"
                  aria-label="Title"
                  className="ck-title-input"
                  autoFocus={!draft.title}
                />
              </Field>

              <div>
                <div className="grid grid-cols-1 sm:grid-cols-2" style={{ gap: '10px', alignItems: 'start' }}>
                  <SearchableSelect
                    label="Client"
                    placeholder="No client"
                    searchPlaceholder="Search clients..."
                    clearLabel="No client"
                    allowClear
                    options={clients.map((c) => ({ value: c.id, label: c.name }))}
                    value={draft.clientId}
                    onChange={(v) =>
                      setDraft((d) => ({ ...d, clientId: v, repoId: v === d.clientId ? d.repoId : null }))
                    }
                  />
                  {draft.clientId ? (
                    reposLoading || repos.length > 0 ? (
                      <SearchableSelect
                        label="Repo"
                        placeholder={reposLoading ? 'Loading repos…' : 'No repo'}
                        searchPlaceholder="Search repos..."
                        clearLabel="No repo"
                        allowClear
                        options={repos.map((r) => ({ value: r.id, label: r.name, hint: r.repo_url }))}
                        value={draft.repoId}
                        onChange={(repoId) => patch({ repoId })}
                      />
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <span className="t-eyebrow">Repo</span>
                        <span
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            minHeight: 'var(--control-md)',
                            fontSize: 'var(--text-sm)',
                            color: 'var(--text-faint)',
                          }}
                        >
                          No repos on file for this client.
                        </span>
                      </div>
                    )
                  ) : null}
                </div>
                <div style={{ marginTop: '6px', fontSize: 'var(--text-xs)', color: 'var(--text-faint)', lineHeight: 1.5 }}>
                  Client is required to queue — Voice lives in their knowledge base. Repo is the publish target, optional until the outline.
                </div>
              </div>

              <Field
                label="Primary search phrase"
                hint="The one phrase this piece is built to rank for."
              >
                <Input
                  icon="search"
                  value={draft.primary}
                  onChange={(e) => patch({ primary: e.target.value })}
                  placeholder="e.g. commercial roofing contractor denver"
                  aria-label="Primary search phrase"
                />
              </Field>

              <Field label="Secondary search phrases" aside={<CountAside count={draft.secondary.length} />}>
                <PhraseField
                  phrases={draft.secondary}
                  onChange={(secondary) => patch({ secondary })}
                  reserved={draft.primary}
                  placeholder="Add a supporting phrase"
                  hint="Enter or comma adds a phrase. Paste a list to add several at once."
                />
              </Field>

              <Field
                label="Context"
                aside={<CountAside count={files.length} />}
                hint="Briefs, outlines, research, brand notes — anything the writer should read first. Files stay with this draft until you leave the page."
              >
                <AttachmentDropzone files={files} error={fileError} onAdd={addFiles} onRemove={removeFile} disabled={queueBusy} />
              </Field>

              <ModelPicker
                variant="field"
                value={draft.model}
                onChange={(model) => patch({ model })}
                disabled={queueBusy}
                hint="Which LLM writes this piece. Default runs on the agent's own model, currently Opus 5.5. Changeable from the queue card until an agent picks it up."
              />
            </div>
          </Card>

          {/* summary */}
          <div style={{ position: 'sticky', top: 'calc(var(--sat, 0px) + var(--space-5))' }}>
            <Card
              eyebrow="Summary"
              title={title || <span style={{ color: 'var(--text-faint)' }}>Untitled piece</span>}
            >
              <div style={{ borderTop: '1px solid var(--border-hairline)' }}>
                <ReadyRow label="Title" value={title ? 'Set' : 'Required'} ok={title.length > 0} />
                <ReadyRow
                  label="Client"
                  value={selectedClient?.name ?? (draft.clientId ? '…' : 'Optional')}
                  ok={!!draft.clientId}
                  optional
                />
                {draft.clientId ? (
                  <ReadyRow
                    label="Repo"
                    value={selectedRepo?.name ?? (draft.repoId ? '…' : 'Optional')}
                    ok={!!draft.repoId}
                    optional
                  />
                ) : null}
                <ReadyRow label="Primary phrase" value={primary || 'Required'} ok={primary.length > 0} />
                <ReadyRow
                  label="Secondary phrases"
                  value={draft.secondary.length > 0 ? plural(draft.secondary.length, 'phrase') : 'Optional'}
                  ok={draft.secondary.length > 0}
                  optional
                />
                <ReadyRow label="Model" value={aiJobModelLabel(draft.model)} ok={!!draft.model} optional />
                <ReadyRow
                  label="Context files"
                  value={files.length > 0 ? `${plural(files.length, 'file')} · ${formatFileSize(totalBytes)}` : 'Optional'}
                  ok={files.length > 0}
                  optional
                />
              </div>

              <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Button
                  icon="zap"
                  disabled={!ready || queueBusy}
                  onClick={handleQueue}
                  title={!ready ? 'Add a title and a primary search phrase' : undefined}
                  style={{ width: '100%' }}
                >
                  {queueBusy ? 'Queuing…' : 'Queue for generation'}
                </Button>
                {queueError ? (
                  <p
                    style={{
                      margin: 0,
                      textAlign: 'center',
                      fontSize: 'var(--text-xs)',
                      color: 'var(--danger-400)',
                      lineHeight: 1.5,
                    }}
                  >
                    {queueError}
                  </p>
                ) : queuedId ? (
                  <p
                    style={{
                      margin: 0,
                      textAlign: 'center',
                      fontSize: 'var(--text-xs)',
                      color: 'var(--text-muted)',
                      lineHeight: 1.5,
                    }}
                  >
                    Queued to the spec queue. Agents will write an outline first.
                  </p>
                ) : (
                  <p
                    style={{
                      margin: 0,
                      textAlign: 'center',
                      fontSize: 'var(--text-xs)',
                      color: 'var(--text-faint)',
                      lineHeight: 1.5,
                    }}
                  >
                    {ready
                      ? selectedClient
                        ? 'Queues an outline job. Client is required — Voice lives in their knowledge base.'
                        : 'Pick a client to queue. Voice lives in their knowledge base.'
                      : 'Add a title and a primary search phrase to complete the brief.'}
                  </p>
                )}
              </div>

              {hasAnything ? (
                <div
                  style={{
                    marginTop: '14px',
                    paddingTop: '12px',
                    borderTop: '1px solid var(--border-hairline)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: confirmClear ? 'space-between' : 'center',
                    gap: '8px',
                  }}
                >
                  {confirmClear ? (
                    <>
                      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>Clear this draft?</span>
                      <span style={{ display: 'inline-flex', gap: '6px' }}>
                        <Button size="sm" variant="danger" onClick={clearDraft}>
                          Clear
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setConfirmClear(false)}>
                          Keep
                        </Button>
                      </span>
                    </>
                  ) : (
                    <Button size="sm" variant="ghost" icon="rotate-ccw" onClick={() => setConfirmClear(true)}>
                      Clear draft
                    </Button>
                  )}
                </div>
              ) : null}
            </Card>
          </div>
        </div>
        )}
      </main>
    </div>
  );
}
